import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Search, Bell, Sun, Moon, Plus } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  CommandDialog, CommandInput, CommandList, CommandItem, CommandGroup, CommandEmpty,
} from "@/components/ui/command";
import { StatusBadge } from "@/components/common/StatusBadge";

export function Topbar({ onMenu, quiet = false }) {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ patients: [], cases: [], reports: [] });
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q.trim()) return setResults({ patients: [], cases: [], reports: [] });
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const unread = notifs.filter((n) => !n.read).length;
  const go = (path) => { setSearchOpen(false); setQ(""); navigate(path); };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#E2E8F0] dark:border-[#223139] bg-white/90 dark:bg-[#07131A]/90 backdrop-blur px-4 lg:px-6">
      <button onClick={onMenu} data-testid="mobile-menu-btn" className="lg:hidden flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E8F0] dark:border-[#223139] text-[#101516] dark:text-white">
        <Menu className="h-5 w-5" />
      </button>

      {/* Search */}
      <button
        onClick={() => setSearchOpen(true)}
        data-testid="global-search-trigger"
        className="flex h-10 flex-1 max-w-md items-center gap-2.5 rounded-xl border border-[#E2E8F0] dark:border-[#223139] bg-[#F8F9F8] dark:bg-[#0E1A21] px-3.5 text-sm text-[#8B9698] transition-colors hover:border-[#00A85A]/40"
      >
        <Search className="h-4 w-4" />
        <span>Search patients, reports...</span>
        <kbd className="ml-auto hidden sm:inline rounded border border-[#E2E8F0] dark:border-[#223139] px-1.5 py-0.5 text-[10px]">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        {!quiet && (
          <Button onClick={() => navigate("/consultation/new")} data-testid="topbar-new-consultation" className="hidden sm:flex bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg h-10">
            <Plus className="h-4 w-4 mr-1.5" /> New Consultation
          </Button>
        )}

        <button onClick={toggle} data-testid="theme-toggle" className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E2E8F0] dark:border-[#223139] text-[#687173] dark:text-[#8B9698] hover:text-[#101516] dark:hover:text-white transition-colors">
          {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="notifications-btn" className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-[#E2E8F0] dark:border-[#223139] text-[#687173] dark:text-[#8B9698] hover:text-[#101516] dark:hover:text-white transition-colors">
              <Bell className="h-[18px] w-[18px]" />
              {unread > 0 && <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#00A85A] text-[9px] font-bold text-white">{unread}</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between border-b border-[#E2E8F0] dark:border-[#223139] px-4 py-3">
              <span className="text-sm font-bold text-[#101516] dark:text-white">Notifications</span>
              <span className="text-xs text-[#8B9698]">{unread} unread</span>
            </div>
            <div className="max-h-80 overflow-y-auto thin-scroll">
              {notifs.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#8B9698]">No notifications</div>}
              {notifs.map((n) => (
                <div key={n.id} className="flex gap-3 border-b border-[#E2E8F0]/60 dark:border-[#223139]/60 px-4 py-3">
                  <div className="mt-1"><StatusBadge status={n.category.toLowerCase() === "ai" ? "info" : "neutral"} label={n.category} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#101516] dark:text-white flex items-center gap-1.5">
                      {n.title}{!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#00A85A]" />}
                    </div>
                    <div className="text-xs text-[#687173] dark:text-[#8B9698]">{n.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#00A85A] text-xs font-bold text-white overflow-hidden">
          {user?.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : initials(user?.name)}
        </div>
      </div>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput placeholder="Search patients, cases, reports..." value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>{q ? "No results found." : "Start typing to search."}</CommandEmpty>
          {results.patients.length > 0 && (
            <CommandGroup heading="Patients">
              {results.patients.map((p) => (
                <CommandItem key={p.id} onSelect={() => go(`/patients/${p.id}`)}>{p.name} · {p.patient_id}</CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.cases.length > 0 && (
            <CommandGroup heading="Cases">
              {results.cases.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/cases/${c.id}`)}>{c.title} · {c.case_id}</CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.reports.length > 0 && (
            <CommandGroup heading="Reports">
              {results.reports.map((r) => (
                <CommandItem key={r.id} onSelect={() => go(`/reports/${r.id}`)}>{r.title}</CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </header>
  );
}
